import { useEffect, useRef, useState } from 'react';

const COUNT_UP_DURATION_MS = 180_000;
const INITIAL_BURST_DURATION_MS = 650;
const TARGET_EXTENSION_MINIMUM_MS = 12_000;
const TARGET_EXTENSION_DELAY_MS = 8_000;
const ZOOM_DURATION_MS = 800;
const SHINE_DURATION_MS = 6_000;
const RESET_FADE_DURATION_MS = 1_000;
const COUNT_UP_START_RATIO = 0.8;
const INITIAL_BURST_TARGET_RATIO = 0.84;

export type CountUpPhase = 'idle' | 'counting' | 'zooming' | 'shining' | 'resetting';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function organicProgress(progress: number): number {
  const slowVariation = Math.sin(progress * Math.PI * 2) * 0.018;
  const subtleVariation = Math.sin(progress * Math.PI * 6) * 0.003;
  return Math.min(Math.max(progress - slowVariation + subtleVariation, 0), 1);
}

function initialBurstProgress(progress: number): number {
  return 1 - ((1 - progress) ** 3);
}

export function useLoopingCountUp(
  target: number | undefined,
  isAnimationEnabled = true,
): { displayedValue: number; phase: CountUpPhase } {
  const [displayedValue, setDisplayedValue] = useState(Number.NaN);
  const [phase, setPhase] = useState<CountUpPhase>('idle');
  const displayedValueRef = useRef(Number.NaN);
  const targetRef = useRef<number | undefined>(target);
  const isAnimationEnabledRef = useRef(isAnimationEnabled);
  const animationFrameIdRef = useRef(0);
  const animateRef = useRef<FrameRequestCallback | null>(null);
  const resetFrameClockRef = useRef<(() => void) | null>(null);
  const resumePhaseRef = useRef<(() => void) | null>(null);
  const hasValidTarget = target !== undefined && Number.isFinite(target) && target >= 0;

  targetRef.current = hasValidTarget ? target : undefined;
  isAnimationEnabledRef.current = isAnimationEnabled;

  useEffect(() => {
    if (!hasValidTarget) {
      displayedValueRef.current = Number.NaN;
      setDisplayedValue(Number.NaN);
      setPhase('idle');
      return;
    }

    if (prefersReducedMotion() && target !== undefined) {
      displayedValueRef.current = target;
      setDisplayedValue(target);
      setPhase('idle');
    }
  }, [hasValidTarget, target]);

  useEffect(() => {
    const initialTarget = targetRef.current;
    if (initialTarget === undefined || !Number.isFinite(initialTarget) || initialTarget < 0) return undefined;
    if (prefersReducedMotion()) return undefined;

    let activePhase: CountUpPhase = 'counting';
    let phaseStartedAt: number | null = null;
    let countStartedAt: number | null = null;
    let countDuration = COUNT_UP_DURATION_MS;
    let countStartValue = initialTarget * COUNT_UP_START_RATIO;
    let countTarget = initialTarget;
    let observedTarget = initialTarget;
    let hasAppliedResetValue = false;
    let isInitialBurst = true;
    let activeTimestamp = 0;
    let previousFrameTimestamp: number | null = null;

    displayedValueRef.current = countStartValue;
    setDisplayedValue(countStartValue);
    setPhase(isAnimationEnabledRef.current ? activePhase : 'idle');

    const updateValue = (value: number) => {
      displayedValueRef.current = value;
      setDisplayedValue(value);
    };

    const scheduleNextFrame = () => {
      if (!isAnimationEnabledRef.current) return;
      animationFrameIdRef.current = window.requestAnimationFrame(animate);
    };

    const animate = (frameTimestamp: number) => {
      animationFrameIdRef.current = 0;
      if (!isAnimationEnabledRef.current) {
        previousFrameTimestamp = null;
        return;
      }

      if (previousFrameTimestamp !== null) {
        activeTimestamp += Math.max(frameTimestamp - previousFrameTimestamp, 0);
      }
      previousFrameTimestamp = frameTimestamp;
      const timestamp = activeTimestamp;
      countStartedAt ??= timestamp;
      const latestTarget = targetRef.current ?? countTarget;

      if (latestTarget > observedTarget) {
        const elapsed = Math.max(timestamp - countStartedAt, 0);
        const remainingDuration = Math.max(countDuration - elapsed, 0);
        countStartValue = displayedValueRef.current;
        countTarget = latestTarget;
        countStartedAt = timestamp;
        countDuration = Math.max(
          remainingDuration + TARGET_EXTENSION_DELAY_MS,
          TARGET_EXTENSION_MINIMUM_MS,
        );
        observedTarget = latestTarget;
        activePhase = 'counting';
        phaseStartedAt = null;
        setPhase('counting');
      }

      if (isInitialBurst) {
        const burstProgress = Math.min((timestamp - countStartedAt) / INITIAL_BURST_DURATION_MS, 1);
        const burstTarget = countTarget * INITIAL_BURST_TARGET_RATIO;
        updateValue(countStartValue + ((burstTarget - countStartValue) * initialBurstProgress(burstProgress)));

        if (burstProgress < 1) {
          scheduleNextFrame();
          return;
        }

        countStartValue = burstTarget;
        countStartedAt = timestamp;
        isInitialBurst = false;
      }

      if (activePhase === 'counting') {
        const progress = Math.min((timestamp - countStartedAt) / countDuration, 1);
        updateValue(countStartValue + ((countTarget - countStartValue) * organicProgress(progress)));

        if (progress >= 1) {
          activePhase = 'zooming';
          phaseStartedAt = timestamp;
          setPhase('zooming');
        }
      } else if (activePhase === 'zooming' && phaseStartedAt !== null) {
        if (timestamp - phaseStartedAt >= ZOOM_DURATION_MS) {
          activePhase = 'shining';
          phaseStartedAt = timestamp;
          setPhase('shining');
        }
      } else if (activePhase === 'shining' && phaseStartedAt !== null) {
        if (timestamp - phaseStartedAt >= SHINE_DURATION_MS) {
          activePhase = 'resetting';
          phaseStartedAt = timestamp;
          hasAppliedResetValue = false;
          setPhase('resetting');
        }
      } else if (activePhase === 'resetting' && phaseStartedAt !== null) {
        const resetElapsed = timestamp - phaseStartedAt;

        if (!hasAppliedResetValue && resetElapsed >= RESET_FADE_DURATION_MS / 2) {
          const nextTarget = targetRef.current ?? countTarget;
          countStartValue = nextTarget * COUNT_UP_START_RATIO;
          countTarget = nextTarget;
          observedTarget = nextTarget;
          updateValue(countStartValue);
          hasAppliedResetValue = true;
        }

        if (resetElapsed >= RESET_FADE_DURATION_MS) {
          countDuration = COUNT_UP_DURATION_MS;
          countStartedAt = timestamp;
          activePhase = 'counting';
          phaseStartedAt = null;
          setPhase('counting');
        }
      }

      scheduleNextFrame();
    };

    animateRef.current = animate;
    resetFrameClockRef.current = () => {
      previousFrameTimestamp = null;
    };
    resumePhaseRef.current = () => setPhase(activePhase);

    if (isAnimationEnabledRef.current) scheduleNextFrame();

    return () => {
      window.cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = 0;
      animateRef.current = null;
      resetFrameClockRef.current = null;
      resumePhaseRef.current = null;
    };
  }, [hasValidTarget]);

  useEffect(() => {
    if (!hasValidTarget || prefersReducedMotion()) return;

    if (!isAnimationEnabled) {
      window.cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = 0;
      resetFrameClockRef.current?.();
      setPhase('idle');
      return;
    }

    resumePhaseRef.current?.();
    if (animationFrameIdRef.current === 0 && animateRef.current) {
      animationFrameIdRef.current = window.requestAnimationFrame(animateRef.current);
    }
  }, [hasValidTarget, isAnimationEnabled]);

  return { displayedValue, phase };
}
